import React, { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LanguageProvider } from '@/contexts/LanguageContext';
import { AutoTranslator } from '@/components/AutoTranslator';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useLocation, useParams } from "react-router-dom";
import { getDomain } from "@/utils/domain";
import { LoadingSpinnerState as LoadingSpinner } from "@/components/ui/LoadingState";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AuthProvider } from '@/contexts/AuthContext';
import { QuickAccountBanner } from '@/components/auth/QuickAccountBanner';
import { AuthorizationProvider } from '@/contexts/AuthorizationContext';
import { useFeatureFlags, getFlag } from '@/config/featureFlags';
import { ThemeProvider } from 'next-themes';
import { RouteNoIndex } from '@/components/seo/RouteNoIndex';

// Phase 1 C6: redirect /talk/deals/:id → /uram/deals/:id (preserve the id).
function TalkDealsIdRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? `/uram/deals/${id}` : '/uram'} replace />;
}

import {
  Index,
  NotFound,
  ServerError,
  Auth,
  Privacy,
  Terms,
  Contact,
  About,
  TrustPromise,
  ResetPassword,
  ForgotPassword,
  AuthCallback,
  Settings,
  Cookies,
  EnHome,
  EnAbout,
  EnContact,
  EnPrivacy,
  EnTerms,
  EnCommunityGuidelines,
  EnHowLavlahWorks,
  EnFoodGuide,
  EnStudentGuide,
  EnBudgetFood,
  EnHowWeRank,
  EnReportCorrections,
  GuidesHome,
  HyamdHoolGuide,
  UramRoundupGuide,
  OyutanZardalGuide,
  EditorialHub,
  EditorialTeam,
  EditorialPolicy,
  CorrectionPolicy,
  FactCheckingPolicy,
  KofeShopGuide,
  RestoranTopGuide,
  FitnessClubGuide,
  GerBulAmraltGuide,
  KaraokeGuide,
  ExtraGuideRoute,
  Dashboard,
  AssistantChat,
  Wallet,
  ExamHub,
  ExamPractice,
  ExamProgress,
  ExamDiagnostic,
  ExamMock,
  ExamPracticeSession,
  ExamStudyPlan,
  ExamMajorGuidance,
  ExamRoadmap,
  ExamSubmitQuestion,
  ExamChallenges,
  ExamChallengeSession,
  ExamChallengeRegister,
  ExamChallengeResults,
  ParentDashboard,
  Subscription,
  EeshLanding,
  Referral,
  FinanceHub,
  FinanceBudgetPlanner,
  FinanceFamilyPlan,
  FinanceDashboard,
  BusinessHub,
  BusinessPlanGenerator,
  BusinessHealthCheck,
  BusinessDashboard,
  BusinessForecast,
  FinanceDebtSavings,
  FinanceLoanBurden,
  AdminHealth,
  AdminDashboard,
  AdminUsers,
  AdminAnonymousAbuse,
  AdminPayments,
  AdminAISettings,
  AdminAIModeration,
  AdminRAGUnified,
  SourceProfileEditor,
  CreditPricing,
  FeatureFlags,
  AdminEmail,
  SecurityCenter,
  AuditLogs,
  ProductionHealth,
  PostLaunch,
  DailyOps,
  AdminFoodIntelligence,
  AdminAIQuality,
  AdminGrowthBrain,
  AdminFacebookContent,
  AdminAdsKeywords,
  AdminCostSafety,
  AdminRAGValueMaximizer,
  AdminWeeklyGrowth,
  AdminPromoCodes,
  AdminExamCoach,
  AdminReferrals,
  AdminEntities,
  TrustHub,
  TrustBanks,
  TrustBankDetail,
  TrustCompare,
  AdminTrustModeration,
  AdminTrustClaims,
  AdminTrustSponsored,
  TrustReviewNew,
  TrustClaim,
  TrustBusinessDashboard,
  TrustReports,
  TrustHouseholdPlanner,
  TrustBusinessAnalytics,
  TrustVertical,
  TrustFinance,
  TrustEntityDetail,
  ValueDiscover,
  ValuePlaceDetail,
  ValueSubmitPlace,
  ValueSubmitPrice,
  ValueSaved,
  ValueCommunityBoard,
  ValueAskAI,
  ValueCategory,
  ValueThankYou,
  ValueRegions,
  ValueRegion,
  Uram,
  ShareLocation,
  FuelIndex,
  FuelStationDetail,
  LocationSharingLanding,
  ViewShare,
  TalkPostDetail,
  TalkNewPost,
  TalkShell,
  TalkDealDetail,
  TalkNewDeal,
  TalkLeaderboardPage,
  AdminCommunity,
  Donate,
  AdminPromotionModeration,
  AdminPlacesIndex,
  AdminPlacesSubmissions,
  AdminPlacesControl,
  AdminEngagementSeed,
  AdminPlacesEdits,
  AdminPlacesReports,
  AdminPlacesDeletions,
  AdminPlacesMerge,
  AdminPlacesProviderUsage,
  AdminPlacesQuality,
  AdminPlacesSocialIngest,
  AdminExcelImport,
  AdminExcelSeedQueue,
  MerchantDashboard,
  MerchantEntityAnalytics,
  MerchantPromote,
  HelpCenter,
  PromoCode,
  Support,
  Notifications,
  Services,
  RequestNew,
  MyRequests,
  RequestDetail,
  AutoHelp,
  TravelRequests,
  ProviderJoin,
  ProviderDashboard,
  ProviderProfile,
  AdminProviderLeads,
  AdminMarketplace,
  Roads,
} from "@/routes/lazyPages";


// Components
import { RouteGuard } from "./components/guards/RouteGuard";
import CookieConsent from "./components/CookieConsent";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { PlatformProvider } from "./contexts/PlatformContext";
import { NavigationProvider } from "./contexts/NavigationContext";
import { UniversalSearchProvider } from "./components/search/UniversalSearchProvider";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const LEGACY_PUBLIC_PREFIXES = ['/exam', '/eesh-2026', '/finance', '/business', '/trust', '/value', '/fuel', '/roads', '/uram', '/talk', '/auto', '/travel', '/assistant', '/wallet', '/subscription', '/pricing', '/promo', '/referral', '/donate', '/merchant', '/dashboard'];

const AppRoutes = () => {
  const { isTemplatesDomain } = getDomain();
  const { pathname } = useLocation();
  const { data: flags, isLoading } = useFeatureFlags();

  if (!isTemplatesDomain && LEGACY_PUBLIC_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
    return <Navigate to="/services" replace />;
  }

  return (
    <>
    <RouteNoIndex />
    <Routes>
      {/* Removed template domain routing - templates feature removed */}
      {isTemplatesDomain ? (
        <>
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/500" element={<ServerError />} />
          {/* English public content pages (also available on templates domain) */}
          <Route path="/en" element={<Suspense fallback={<LoadingSpinner />}><EnHome /></Suspense>} />
          <Route path="/en/about" element={<Suspense fallback={<LoadingSpinner />}><EnAbout /></Suspense>} />
          <Route path="/en/contact" element={<Suspense fallback={<LoadingSpinner />}><EnContact /></Suspense>} />
          <Route path="/en/privacy" element={<Suspense fallback={<LoadingSpinner />}><EnPrivacy /></Suspense>} />
          <Route path="/en/terms" element={<Suspense fallback={<LoadingSpinner />}><EnTerms /></Suspense>} />
          <Route path="/en/community-guidelines" element={<Suspense fallback={<LoadingSpinner />}><EnCommunityGuidelines /></Suspense>} />
          <Route path="/en/how-lavlah-works" element={<Suspense fallback={<LoadingSpinner />}><EnHowLavlahWorks /></Suspense>} />
          <Route path="/en/ulaanbaatar-food-guide" element={<Suspense fallback={<LoadingSpinner />}><EnFoodGuide /></Suspense>} />
          <Route path="/en/ulaanbaatar-student-guide" element={<Suspense fallback={<LoadingSpinner />}><EnStudentGuide /></Suspense>} />
          <Route path="/en/budget-food-in-ulaanbaatar" element={<Suspense fallback={<LoadingSpinner />}><EnBudgetFood /></Suspense>} />
          <Route path="/en/how-we-rank-places" element={<Suspense fallback={<LoadingSpinner />}><EnHowWeRank /></Suspense>} />
          <Route path="/en/report-corrections" element={<Suspense fallback={<LoadingSpinner />}><EnReportCorrections /></Suspense>} />
          {/* Mongolian editorial guides (AdSense-eligible) */}
          <Route path="/guides" element={<Suspense fallback={<LoadingSpinner />}><GuidesHome /></Suspense>} />
          <Route path="/guides/hyamd-hool-ulaanbaatar" element={<Suspense fallback={<LoadingSpinner />}><HyamdHoolGuide /></Suspense>} />
          <Route path="/guides/uram-deals-roundup" element={<Suspense fallback={<LoadingSpinner />}><UramRoundupGuide /></Suspense>} />
          <Route path="/guides/oyutan-amidral-zardal" element={<Suspense fallback={<LoadingSpinner />}><OyutanZardalGuide /></Suspense>} />
          {/* Editorial E-E-A-T pages (publisher identity for AdSense) */}
          <Route path="/editorial" element={<Suspense fallback={<LoadingSpinner />}><EditorialHub /></Suspense>} />
          <Route path="/editorial/team" element={<Suspense fallback={<LoadingSpinner />}><EditorialTeam /></Suspense>} />
          <Route path="/editorial-policy" element={<Suspense fallback={<LoadingSpinner />}><EditorialPolicy /></Suspense>} />
          <Route path="/correction-policy" element={<Suspense fallback={<LoadingSpinner />}><CorrectionPolicy /></Suspense>} />
          <Route path="/fact-checking-policy" element={<Suspense fallback={<LoadingSpinner />}><FactCheckingPolicy /></Suspense>} />
          <Route path="/guides/ulaanbaatar-kofe-shop" element={<Suspense fallback={<LoadingSpinner />}><KofeShopGuide /></Suspense>} />
          <Route path="/guides/ulaanbaatar-restoran-top" element={<Suspense fallback={<LoadingSpinner />}><RestoranTopGuide /></Suspense>} />
          <Route path="/guides/fitness-club-une" element={<Suspense fallback={<LoadingSpinner />}><FitnessClubGuide /></Suspense>} />
          <Route path="/guides/ger-bul-amraltyn-gazar" element={<Suspense fallback={<LoadingSpinner />}><GerBulAmraltGuide /></Suspense>} />
          <Route path="/guides/karaoke-bar-ub" element={<Suspense fallback={<LoadingSpinner />}><KaraokeGuide /></Suspense>} />
          <Route path="/guides/:slug" element={<Suspense fallback={<LoadingSpinner />}><ExtraGuideRoute /></Suspense>} />
          <Route path="*" element={<NotFound />} />
        </>
      ) : (
        <>
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/callback" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <AuthCallback />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/auth/forgot" element={<ForgotPassword />} />
          <Route path="/auth/reset" element={<ResetPassword />} />
          <Route path="/reset-password" element={<Navigate to="/auth/reset" replace />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/settings" element={<RouteGuard requireAuth><Settings /></RouteGuard>} />
          <Route path="/promo" element={<PromoCode />} />
          <Route path="/about/trust" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <TrustPromise />
              </Suspense>
            </ErrorBoundary>
          } />
          
          {/* Exam Coach Routes */}
          <Route path="/exam" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <ExamHub />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/exam/practice" element={
            <RouteGuard requireAuth>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <ExamPractice />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/exam/progress" element={
            <RouteGuard requireAuth>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <ExamProgress />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/exam/diagnostic" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <ExamDiagnostic />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/exam/practice/session" element={
            <RouteGuard requireAuth>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <ExamPracticeSession />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/exam/mock" element={
            <RouteGuard requireAuth>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <ExamMock />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />

          {/* New Exam Coach Features */}
          <Route path="/exam/study-plan" element={
            <RouteGuard requireAuth>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <ExamStudyPlan />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/exam/major-guidance" element={
            <RouteGuard requireAuth>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <ExamMajorGuidance />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/exam/roadmap" element={
            <RouteGuard requireAuth>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <ExamRoadmap />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/exam/submit-question" element={
            <RouteGuard requireAuth>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <ExamSubmitQuestion />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/exam/challenges" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <ExamChallenges />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/exam/challenges/:challengeId" element={
            <RouteGuard requireAuth>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <ExamChallengeSession />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/exam/challenges/:challengeId/register" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <ExamChallengeRegister />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/exam/challenges/:challengeId/results/:attemptId" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <ExamChallengeResults />
              </Suspense>
            </ErrorBoundary>
          } />

          {/* Advisory Platform - Finance & Business */}
          <Route path="/finance" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <FinanceHub />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/finance/budget" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <FinanceBudgetPlanner />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/finance/family-plan" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <FinanceFamilyPlan />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/finance/debt-savings" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <FinanceDebtSavings />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/finance/loan-check" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <FinanceLoanBurden />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/finance/dashboard" element={
            <RouteGuard requireAuth>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <FinanceDashboard />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/business" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <BusinessHub />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/business/plan" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <BusinessPlanGenerator />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/business/health-check" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <BusinessHealthCheck />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/business/forecast" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <BusinessForecast />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/business/dashboard" element={
            <RouteGuard requireAuth>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <BusinessDashboard />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />

          {/* Google Ads Landing Pages */}
          <Route path="/eesh-2026" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <EeshLanding />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/eesh-2026-en" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <EeshLanding locale="en" />
              </Suspense>
            </ErrorBoundary>
          } />

          {/* ===== Marketplace — universal local requests & on-demand services ===== */}
          <Route path="/roads" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><Roads /></Suspense></ErrorBoundary>
          } />
          <Route path="/services" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><Services /></Suspense></ErrorBoundary>
          } />
          <Route path="/request/new" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><RequestNew /></Suspense></ErrorBoundary>
          } />
          <Route path="/requests" element={
            <RouteGuard requireAuth>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><MyRequests /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/requests/:id" element={
            <RouteGuard requireAuth>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><RequestDetail /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/auto" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AutoHelp /></Suspense></ErrorBoundary>
          } />
          <Route path="/auto/:sub" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AutoHelp /></Suspense></ErrorBoundary>
          } />
          <Route path="/travel" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><TravelRequests /></Suspense></ErrorBoundary>
          } />
          <Route path="/travel/:sub" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><TravelRequests /></Suspense></ErrorBoundary>
          } />
          <Route path="/provider/join" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><ProviderJoin /></Suspense></ErrorBoundary>
          } />
          <Route path="/provider/:id" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><ProviderProfile /></Suspense></ErrorBoundary>
          } />
          <Route path="/provider" element={
            <RouteGuard requireAuth>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><ProviderDashboard /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/marketplace" element={
            <RouteGuard requireAuth requireAdmin>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminMarketplace /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/provider-leads" element={
            <RouteGuard requireAuth requireAdmin>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminProviderLeads /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />


          {/* AI Chat Routes - GPT-5 with Mongolian RAG */}
          <Route path="/assistant" element={<Navigate to="/assistant/chat" replace />} />
          <Route path="/assistant/chat" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <AssistantChat />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/assistant/chat/:conversationId" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <AssistantChat />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/wallet" element={
            <RouteGuard requireAuth>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <Wallet />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          {/* MockPayment route removed from production for security */}
          {/* promo route already defined on line 108 */}
          
          {/* Subscription Management */}
          <Route path="/subscription" element={
            <RouteGuard requireAuth>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <Subscription />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />

          {/* /pricing previously 404'd — keep the URL alive as a redirect */}
          <Route path="/pricing" element={<Navigate to="/subscription" replace />} />

          
          <Route path="/dashboard" element={<RouteGuard requireAuth><Dashboard /></RouteGuard>} />
          <Route path="/referral" element={
            <RouteGuard requireAuth>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <Referral />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/account/security" element={<Navigate to="/settings?tab=security" replace />} />
          
          {/* User Notifications */}
          <Route path="/notifications" element={
            <RouteGuard requireAuth>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <Notifications />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          
          <Route path="/support" element={
            <RouteGuard requireAuth>
              <Support />
            </RouteGuard>
          } />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin/index" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin/wallet" element={<Navigate to="/admin/users" replace />} />
          <Route path="/admin/payment/settings" element={<Navigate to="/admin/payments?tab=settings" replace />} />
          <Route path="/admin/test-sources" element={<Navigate to="/admin/rag-unified?tab=test" replace />} />
          <Route path="/admin/analytics" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin/logs" element={<Navigate to="/admin/audit-logs" replace />} />
          <Route path="/admin/data-engine" element={<Navigate to="/admin/rag-unified?tab=tools" replace />} />
          
          <Route path="/admin/health" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminHealth />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          
          <Route path="/admin/production-health" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <ProductionHealth />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />

          <Route path="/admin/post-launch" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <PostLaunch />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />

          <Route path="/admin/daily-ops" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <DailyOps />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />

          <Route path="/admin/dashboard" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminDashboard />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          {/* Redirect old assistant settings to unified AI settings */}
          <Route path="/admin/assistant/settings" element={<Navigate to="/admin/ai-settings" replace />} />
          <Route path="/admin/ai-settings" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminAISettings />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/users" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminUsers />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/anonymous-abuse" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminAnonymousAbuse />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/payments" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminPayments />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/promo-codes" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminPromoCodes />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          {/* Redirect old RAG route to unified page */}
          <Route path="/admin/rag" element={<Navigate to="/admin/rag-unified" replace />} />
          
          <Route path="/admin/rag-unified" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminRAGUnified />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/rag-sources/edit/:id" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <SourceProfileEditor />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          
          {/* Advanced Admin Tools (Phase 7+) - Feature flag protected */}
          {/* Unified Security Center - replaces old Security, RLS Sanity, and Secrets Audit pages */}
          <Route path="/admin/security-center" element={
            <RouteGuard requireAdmin requireFlag="enable_security_scan">
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <SecurityCenter />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          {/* Redirect old security pages to unified Security Center */}
          <Route path="/admin/security" element={<Navigate to="/admin/security-center" replace />} />
          <Route path="/admin/rls-sanity" element={<Navigate to="/admin/security-center?tab=rls" replace />} />
          <Route path="/admin/secrets-audit" element={<Navigate to="/admin/security-center?tab=secrets" replace />} />
          
          <Route path="/admin/audit-logs" element={
            <RouteGuard requireAdmin requireFlag="enable_audit_logs">
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AuditLogs />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          {/* Redirect old AI Monitoring to unified AI Settings */}
          <Route path="/admin/ai-monitoring" element={<Navigate to="/admin/ai-settings" replace />} />
          <Route path="/admin/system-health" element={<Navigate to="/admin/health" replace />} />
          {/* AI Quality Dashboard - Self-improving evaluation system */}
          <Route path="/admin/ai-quality" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminAIQuality />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/ai-moderation" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminAIModeration />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/food-intelligence" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminFoodIntelligence />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          {/* Growth Brain Dashboard - AI-powered growth insights */}
          <Route path="/admin/growth-brain" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminGrowthBrain />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          {/* Weekly Growth Optimizer - Self-improving growth loop */}
          <Route path="/admin/growth-optimizer" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminWeeklyGrowth />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          {/* Facebook Content Selector - AI-powered social media content */}
          <Route path="/admin/facebook-content" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminFacebookContent />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          {/* Google Ads Keywords - High-intent keyword analysis */}
          <Route path="/admin/ads-keywords" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminAdsKeywords />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          {/* Cost Safety Monitor - Cost vs revenue guardrail */}
          <Route path="/admin/cost-safety" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminCostSafety />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          {/* RAG Value Maximizer - Optimize Mongolian data advantage */}
          <Route path="/admin/rag-value" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminRAGValueMaximizer />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          {/* RAG Quality removed - functionality merged into /admin/rag-unified */}
          <Route path="/admin/rag-quality" element={<Navigate to="/admin/rag-unified" replace />} />
          <Route path="/admin/credit-pricing" element={
            <RouteGuard requireAdmin requireFlag="enable_credit_pricing">
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <CreditPricing />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/reports/rag-costs" element={<Navigate to="/admin/rag-unified?tab=analytics" replace />} />
          
          {/* Backward compatibility redirect */}
          <Route path="/admin/audit" element={<Navigate to="/admin/audit-logs" replace />} />
          
          {/* Email System Management */}
          <Route path="/admin/email" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminEmail />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />

          {/* Feature Flags Management */}
          <Route path="/admin/feature-flags" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <FeatureFlags />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />

          {/* Exam Coach Admin Panel */}
          <Route path="/admin/exam-coach" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminExamCoach />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />

          {/* Referral Management */}
          <Route path="/admin/referrals" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminReferrals />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />

          {/* Trust Platform Admin */}
          <Route path="/admin/entities" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminEntities />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />

          {/* Trust Platform Public */}
          <Route path="/trust" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <TrustHub />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/trust/banks" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <TrustBanks />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/trust/banks/:slug" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <TrustBankDetail />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/trust/compare" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <TrustCompare />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/admin/trust/moderation" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminTrustModeration />
                </Suspense>
              </ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/trust/claims" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminTrustClaims /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/trust/sponsored" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminTrustSponsored /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/trust/review/new" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><TrustReviewNew /></Suspense></ErrorBoundary>
          } />
          <Route path="/trust/claim" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><TrustClaim /></Suspense></ErrorBoundary>
          } />
          <Route path="/trust/business" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><TrustBusinessDashboard /></Suspense></ErrorBoundary>
          } />
          {/* Phase 4 routes */}
          <Route path="/trust/reports" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><TrustReports /></Suspense></ErrorBoundary>
          } />
          <Route path="/trust/household" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><TrustHouseholdPlanner /></Suspense></ErrorBoundary>
          } />
          <Route path="/trust/analytics" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><TrustBusinessAnalytics /></Suspense></ErrorBoundary>
          } />
          <Route path="/trust/v/:vertical" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><TrustVertical /></Suspense></ErrorBoundary>
          } />
          <Route path="/trust/v/:vertical/:slug" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><TrustEntityDetail /></Suspense></ErrorBoundary>
          } />
          <Route path="/trust/finance" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><TrustFinance /></Suspense></ErrorBoundary>
          } />

          {/* Value Map */}
          <Route path="/value" element={<Navigate to="/" replace />} />
          <Route path="/value/browse" element={<Navigate to="/" replace />} />
          {/* Single canonical map surface — homepage IS the map. */}
          <Route path="/value/map" element={<Navigate to="/" replace />} />
          <Route path="/value/place/:slug" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><ValuePlaceDetail /></Suspense></ErrorBoundary>
          } />
          <Route path="/value/place/:slug/price" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><ValueSubmitPrice /></Suspense></ErrorBoundary>
          } />
          <Route path="/value/submit/place" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><ValueSubmitPlace /></Suspense></ErrorBoundary>
          } />
          <Route path="/value/saved" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><ValueSaved /></Suspense></ErrorBoundary>
          } />
          <Route path="/value/board" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><ValueCommunityBoard /></Suspense></ErrorBoundary>
          } />
          <Route path="/value/ask" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><ValueAskAI /></Suspense></ErrorBoundary>
          } />
          <Route path="/value/ask/legacy" element={<Navigate to="/value/ask" replace />} />
          <Route path="/uram" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><Uram /></Suspense></ErrorBoundary>
          } />
          <Route path="/share-location" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><ShareLocation /></Suspense></ErrorBoundary>
          } />
          <Route path="/fuel" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><FuelIndex /></Suspense></ErrorBoundary>
          } />
          <Route path="/fuel/:slug" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><FuelStationDetail /></Suspense></ErrorBoundary>
          } />
          <Route path="/location-sharing" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><LocationSharingLanding /></Suspense></ErrorBoundary>
          } />
          <Route path="/l/:shareId" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><ViewShare /></Suspense></ErrorBoundary>
          } />
          <Route path="/talk" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><TalkShell /></Suspense></ErrorBoundary>
          } />
          <Route path="/talk/new" element={
            <RouteGuard requireAuth>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><TalkNewPost /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/talk/leaderboard" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><TalkLeaderboardPage /></Suspense></ErrorBoundary>
          } />
          {/* Hot deals moved to /uram/* — keep old /talk/deals/* as redirects.
              Phase 1 C6: preserve the :id so deep links don't drop the user
              on the Uram feed homepage. */}
          <Route path="/talk/deals/new" element={<Navigate to="/uram/deals/new" replace />} />
          <Route path="/talk/deals/:id" element={<TalkDealsIdRedirect />} />
          <Route path="/talk/deals" element={<Navigate to="/uram" replace />} />
          <Route path="/uram/deals/new" element={
            <RouteGuard requireAuth>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><TalkNewDeal /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/uram/deals/:id" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><TalkDealDetail /></Suspense></ErrorBoundary>
          } />
          <Route path="/talk/discussions" element={<Navigate to="/talk" replace />} />
          <Route path="/talk/:id" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><TalkPostDetail /></Suspense></ErrorBoundary>
          } />
          <Route path="/value/category/:slug" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><ValueCategory /></Suspense></ErrorBoundary>
          } />
          <Route path="/value/thanks" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><ValueThankYou /></Suspense></ErrorBoundary>
          } />
          <Route path="/value/regions" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><ValueRegions /></Suspense></ErrorBoundary>
          } />
          <Route path="/value/region/:slug" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><ValueRegion /></Suspense></ErrorBoundary>
          } />
          <Route path="/install" element={<Navigate to="/" replace />} />
          <Route path="/donate" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><Donate /></Suspense></ErrorBoundary>
          } />
          <Route path="/merchant" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><MerchantDashboard /></Suspense></ErrorBoundary>
          } />
          <Route path="/merchant/entity/:id" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><MerchantEntityAnalytics /></Suspense></ErrorBoundary>
          } />
          <Route path="/merchant/entity/:id/promote" element={
            <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><MerchantPromote /></Suspense></ErrorBoundary>
          } />
          {/* Legacy moderation route — redirects to canonical Places submissions queue */}
          <Route path="/admin/value/moderation" element={<Navigate to="/admin/places/submissions" replace />} />
          <Route path="/admin/value/promotions" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminPromotionModeration /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          {/* Places Moderation v2 */}
          <Route path="/admin/places" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminPlacesControl /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/places/control" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminPlacesControl /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/places/engagement" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminEngagementSeed /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/places/legacy" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminPlacesIndex /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/places/submissions" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminPlacesSubmissions /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/places/edits" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminPlacesEdits /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/places/reports" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminPlacesReports /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/places/deletions" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminPlacesDeletions /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/places/merge" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminPlacesMerge /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/places/provider-usage" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminPlacesProviderUsage /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/places/quality" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminPlacesQuality /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/places/social-ingest" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminPlacesSocialIngest /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/places/excel-import" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminExcelImport /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/places/excel-queue" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminExcelSeedQueue /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          <Route path="/admin/community" element={
            <RouteGuard requireAdmin>
              <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminCommunity /></Suspense></ErrorBoundary>
            </RouteGuard>
          } />
          {/* Phase 1: Disabled routes - uncomment to re-enable */}
          {/* <Route path="/admin/promo-codes" element={...} /> */}
          {/* <Route path="/admin/moderation" element={...} /> */}
          {/* <Route path="/admin/support" element={...} /> */}
          {/* <Route path="/admin/data/mongolian" element={...} /> */}
          {/* <Route path="/admin/system/settings" element={...} /> */}

          {/* Parent Dashboard - Read-only via secure token */}
          <Route path="/parent/:token" element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <ParentDashboard />
              </Suspense>
            </ErrorBoundary>
          } />

          {/* Help Pages */}
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/faq" element={<Navigate to="/help" replace />} />

          {/* English public content pages (AdSense / SEO) */}
          <Route path="/en" element={<Suspense fallback={<LoadingSpinner />}><EnHome /></Suspense>} />
          <Route path="/en/about" element={<Suspense fallback={<LoadingSpinner />}><EnAbout /></Suspense>} />
          <Route path="/en/contact" element={<Suspense fallback={<LoadingSpinner />}><EnContact /></Suspense>} />
          <Route path="/en/privacy" element={<Suspense fallback={<LoadingSpinner />}><EnPrivacy /></Suspense>} />
          <Route path="/en/terms" element={<Suspense fallback={<LoadingSpinner />}><EnTerms /></Suspense>} />
          <Route path="/en/community-guidelines" element={<Suspense fallback={<LoadingSpinner />}><EnCommunityGuidelines /></Suspense>} />
          <Route path="/en/how-lavlah-works" element={<Suspense fallback={<LoadingSpinner />}><EnHowLavlahWorks /></Suspense>} />
          <Route path="/en/ulaanbaatar-food-guide" element={<Suspense fallback={<LoadingSpinner />}><EnFoodGuide /></Suspense>} />
          <Route path="/en/ulaanbaatar-student-guide" element={<Suspense fallback={<LoadingSpinner />}><EnStudentGuide /></Suspense>} />
          <Route path="/en/budget-food-in-ulaanbaatar" element={<Suspense fallback={<LoadingSpinner />}><EnBudgetFood /></Suspense>} />
          <Route path="/en/how-we-rank-places" element={<Suspense fallback={<LoadingSpinner />}><EnHowWeRank /></Suspense>} />
          <Route path="/en/report-corrections" element={<Suspense fallback={<LoadingSpinner />}><EnReportCorrections /></Suspense>} />

          {/* Mongolian editorial guides (AdSense-eligible) */}
          <Route path="/guides" element={<Suspense fallback={<LoadingSpinner />}><GuidesHome /></Suspense>} />
          <Route path="/guides/hyamd-hool-ulaanbaatar" element={<Suspense fallback={<LoadingSpinner />}><HyamdHoolGuide /></Suspense>} />
          <Route path="/guides/uram-deals-roundup" element={<Suspense fallback={<LoadingSpinner />}><UramRoundupGuide /></Suspense>} />
          <Route path="/guides/oyutan-amidral-zardal" element={<Suspense fallback={<LoadingSpinner />}><OyutanZardalGuide /></Suspense>} />

          {/* Editorial E-E-A-T pages (publisher identity for AdSense) */}
          <Route path="/editorial" element={<Suspense fallback={<LoadingSpinner />}><EditorialHub /></Suspense>} />
          <Route path="/editorial/team" element={<Suspense fallback={<LoadingSpinner />}><EditorialTeam /></Suspense>} />
          <Route path="/editorial-policy" element={<Suspense fallback={<LoadingSpinner />}><EditorialPolicy /></Suspense>} />
          <Route path="/correction-policy" element={<Suspense fallback={<LoadingSpinner />}><CorrectionPolicy /></Suspense>} />
          <Route path="/fact-checking-policy" element={<Suspense fallback={<LoadingSpinner />}><FactCheckingPolicy /></Suspense>} />
          <Route path="/guides/ulaanbaatar-kofe-shop" element={<Suspense fallback={<LoadingSpinner />}><KofeShopGuide /></Suspense>} />
          <Route path="/guides/ulaanbaatar-restoran-top" element={<Suspense fallback={<LoadingSpinner />}><RestoranTopGuide /></Suspense>} />
          <Route path="/guides/fitness-club-une" element={<Suspense fallback={<LoadingSpinner />}><FitnessClubGuide /></Suspense>} />
          <Route path="/guides/ger-bul-amraltyn-gazar" element={<Suspense fallback={<LoadingSpinner />}><GerBulAmraltGuide /></Suspense>} />
          <Route path="/guides/karaoke-bar-ub" element={<Suspense fallback={<LoadingSpinner />}><KaraokeGuide /></Suspense>} />
          <Route path="/guides/:slug" element={<Suspense fallback={<LoadingSpinner />}><ExtraGuideRoute /></Suspense>} />

          <Route path="*" element={<NotFound />} />
        </>
      )}
    </Routes>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <AuthorizationProvider>
        <PlatformProvider>
          <LanguageProvider>
            <TooltipProvider>
              <AutoTranslator />
              {/* Skip Navigation Link */}
              <a 
                href="#main-content" 
                className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                Skip to main content
              </a>
              
              <Toaster />
              <Sonner />
              <CookieConsent />
              <PWAInstallPrompt />
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <NavigationProvider>
                  <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingSpinner />}>
                        <main id="main-content">
                          <QuickAccountBanner />
                          <UniversalSearchProvider>
                            <AppRoutes />
                          </UniversalSearchProvider>
                        </main>
                      </Suspense>
                    </ErrorBoundary>
                  </ThemeProvider>
                </NavigationProvider>
              </BrowserRouter>
            </TooltipProvider>
          </LanguageProvider>
        </PlatformProvider>
      </AuthorizationProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
